import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Compartment, EditorState } from '@codemirror/state';
import { EditorView, keymap, lineNumbers } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { yaml } from '@codemirror/lang-yaml';
import { searchKeymap, highlightSelectionMatches } from '@codemirror/search';
import { PageHeader } from '../components/PageHeader';
import { LoadingDisplay } from '../components/LoadingDisplay';
import { useApiFetch } from '../hooks/useApiFetch';
import { useIsDarkTheme } from '../hooks/useIsDarkTheme';
import { useViewers } from '../hooks/useViewers';
import { useJobConfig } from '../hooks/useJobConfig';
import { parseYaml, dumpYaml } from '../utils/yaml';
import { responseDetail } from '../utils/responseDetail';

const DEFAULT_YAML = `tasks:
  - package: ""
    name: ""
    items:
      - name: ""
models:
  - package: ""
    name: ""
    items:
      - name: ""
limit: 1
`;

interface FormFields {
  eval_set_id: string;
  name: string;
  limit: string;
  epochs: string;
}

interface SecretDeclaration {
  name: string;
  description?: string;
}

function extractSecrets(config: Record<string, unknown>): SecretDeclaration[] {
  const secretsMap = new Map<string, SecretDeclaration>();

  const addSecrets = (arr: unknown) => {
    if (!Array.isArray(arr)) return;
    for (const s of arr) {
      if (s && typeof s === 'object' && 'name' in s) {
        secretsMap.set(s.name as string, s as SecretDeclaration);
      }
    }
  };

  const runner = config.runner as Record<string, unknown> | undefined;
  addSecrets(runner?.secrets);

  const tasks = config.tasks as Record<string, unknown>[] | undefined;
  if (Array.isArray(tasks)) {
    for (const task of tasks) {
      const items = task.items as Record<string, unknown>[] | undefined;
      if (Array.isArray(items)) {
        for (const item of items) {
          addSecrets(item.secrets);
        }
      }
    }
  }

  return Array.from(secretsMap.values());
}

interface CreateEvalSetResponse {
  eval_set_id?: string;
  id?: string;
}

/** Recursively strip null values and empty containers from a config object. */
function stripDefaults(obj: unknown): unknown {
  if (Array.isArray(obj)) {
    const filtered = obj.map(stripDefaults).filter(v => v != null);
    return filtered.length > 0 ? filtered : undefined;
  }
  if (obj && typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      const stripped = stripDefaults(value);
      if (stripped != null) {
        result[key] = stripped;
      }
    }
    return Object.keys(result).length > 0 ? result : undefined;
  }
  return obj;
}

function extractFormFields(obj: Record<string, unknown>): FormFields {
  return {
    eval_set_id: String(obj.eval_set_id ?? ''),
    name: String(obj.name ?? ''),
    limit: obj.limit != null ? String(obj.limit) : '',
    epochs: obj.epochs != null ? String(obj.epochs) : '',
  };
}

function applyFormFieldToConfig(
  config: Record<string, unknown>,
  field: keyof FormFields,
  value: string
): Record<string, unknown> {
  const updated = { ...config };

  switch (field) {
    case 'eval_set_id':
      if (value) {
        updated.eval_set_id = value;
      } else {
        delete updated.eval_set_id;
      }
      break;

    case 'name':
      if (value) {
        updated.name = value;
      } else {
        delete updated.name;
      }
      break;

    case 'limit': {
      const n = Number(value);
      if (value && !Number.isNaN(n)) {
        updated.limit = n;
      } else {
        delete updated.limit;
      }
      break;
    }

    case 'epochs': {
      const n = Number(value);
      if (value && !Number.isNaN(n)) {
        updated.epochs = n;
      } else {
        delete updated.epochs;
      }
      break;
    }
  }

  return updated;
}

/** Read the current YAML text from the CodeMirror editor, or fall back to state. */
function getEditorText(viewRef: React.RefObject<EditorView | null>): string {
  return viewRef.current?.state.doc.toString() ?? '';
}

/**
 * CodeMirror theme for the YAML editor. CodeMirror styles its own surface
 * (it isn't reachable by Tailwind's `dark:`), so we feed it explicit colors
 * and the `dark` flag. Swapped at runtime via a Compartment on theme change.
 */
const editorTheme = (dark: boolean) => {
  const styles: Record<string, Record<string, string>> = {
    '&': { height: '100%' },
    '.cm-scroller': { overflow: 'auto' },
    '.cm-content': { fontFamily: 'monospace', fontSize: '13px' },
    '.cm-gutters': {
      backgroundColor: 'var(--color-surface-2)',
      color: 'var(--color-muted)',
      borderRight: '1px solid var(--color-line)',
    },
  };
  if (dark) {
    styles['&'].backgroundColor = 'var(--color-surface)';
    styles['&'].color = 'var(--color-ink)';
  }
  return EditorView.theme(styles, { dark });
};

export default function LaunchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const cloneId = searchParams.get('clone');
  const {
    config: clonedConfig,
    isLoading: cloneLoading,
    error: cloneError,
    retry: retryClone,
  } = useJobConfig('eval-set', cloneId);
  // A clone that failed to load blocks launching until the user picks retry or
  // an explicit fresh start; the template must not stand in for it silently.
  const cloneBlocked =
    cloneId !== null && (cloneLoading || cloneError !== null);

  const [fields, setFields] = useState<FormFields>({
    eval_set_id: '',
    name: '',
    limit: '1',
    epochs: '',
  });
  const [yamlText, setYamlText] = useState(DEFAULT_YAML);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [detectedSecrets, setDetectedSecrets] = useState<SecretDeclaration[]>(
    []
  );
  const [secretValues, setSecretValues] = useState<Record<string, string>>({});
  const [yamlParseError, setYamlParseError] = useState<string | null>(null);

  const editorRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const themeCompartment = useRef(new Compartment());
  // Latest resolved dark/light, read when (re)creating the editor without
  // making the editor's init effect depend on the theme. Seeded from the live
  // attribute so the first editor mount isn't a frame behind the theme.
  const isDarkRef = useRef(
    document.documentElement.getAttribute('data-bs-theme') === 'dark'
  );
  const syncSourceRef = useRef<'form' | 'editor' | null>(null);
  const submittingRef = useRef(false);
  const yamlTextRef = useRef(yamlText);

  const { apiFetch } = useApiFetch();
  const { primaryUrl } = useViewers();
  const isDark = useIsDarkTheme();

  // Keep yamlTextRef in sync so the editor init always reads the latest value
  useEffect(() => {
    yamlTextRef.current = yamlText;
  }, [yamlText]);

  // Initialize with cloned config — retries if editor not yet mounted
  const [prevCloneId, setPrevCloneId] = useState<string | null>(null);
  if (cloneId && clonedConfig && prevCloneId !== cloneId) {
    setPrevCloneId(cloneId);

    const configCopy = (stripDefaults({
      ...clonedConfig,
      eval_set_id: undefined,
    }) ?? {}) as Record<string, unknown>;

    const newYaml = dumpYaml(configCopy);
    setYamlText(newYaml);
    setFields(extractFormFields(configCopy));
    setDetectedSecrets(extractSecrets(configCopy));
  }

  // Sync editor content when clone initializes
  useEffect(() => {
    if (!cloneId || !clonedConfig || !viewRef.current) return;

    const configCopy = (stripDefaults({
      ...clonedConfig,
      eval_set_id: undefined,
    }) ?? {}) as Record<string, unknown>;

    const newYaml = dumpYaml(configCopy);
    const view = viewRef.current;
    syncSourceRef.current = 'form';
    view.dispatch({
      changes: { from: 0, to: view.state.doc.length, insert: newYaml },
    });
    syncSourceRef.current = null;
  }, [cloneId, clonedConfig]);

  // Initialize CodeMirror
  useEffect(() => {
    if (!editorRef.current || viewRef.current) return;

    const state = EditorState.create({
      doc: yamlTextRef.current,
      extensions: [
        lineNumbers(),
        history(),
        yaml(),
        EditorView.contentAttributes.of({ 'aria-label': 'YAML configuration' }),
        highlightSelectionMatches(),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        keymap.of([...defaultKeymap, ...historyKeymap, ...searchKeymap] as any),
        EditorView.updateListener.of(update => {
          if (update.docChanged) {
            const newText = update.state.doc.toString();
            if (syncSourceRef.current === 'form') return;
            syncSourceRef.current = 'editor';
            setYamlText(newText);
            try {
              const parsed = parseYaml(newText);
              setYamlParseError(null);
              if (parsed && typeof parsed === 'object') {
                const obj = parsed as Record<string, unknown>;
                setFields(extractFormFields(obj));
                setDetectedSecrets(extractSecrets(obj));
              }
            } catch (err) {
              setYamlParseError(
                err instanceof Error ? err.message : 'Invalid YAML'
              );
            }
            syncSourceRef.current = null;
          }
        }),
        // Seed with the current theme; the effect below keeps it in sync.
        // `isDarkRef` (not `isDark`) so a theme flip doesn't recreate the
        // editor and discard undo history / cursor.
        themeCompartment.current.of(editorTheme(isDarkRef.current)),
      ],
    });

    viewRef.current = new EditorView({ state, parent: editorRef.current });

    return () => {
      viewRef.current?.destroy();
      viewRef.current = null;
    };
    // Re-run when cloneLoading changes (editor div isn't in DOM during loading state)
  }, [cloneLoading]);

  // Follow the global theme (useIsDarkTheme observes [data-bs-theme]): swap the
  // editor's theme compartment whenever the resolved dark/light flips, and keep
  // isDarkRef current so a later editor re-create seeds the right theme.
  useEffect(() => {
    isDarkRef.current = isDark;
    viewRef.current?.dispatch({
      effects: themeCompartment.current.reconfigure(editorTheme(isDark)),
    });
  }, [isDark]);

  const handleFieldChange = useCallback(
    (field: keyof FormFields, value: string) => {
      setFields(prev => ({ ...prev, [field]: value }));

      try {
        const currentText = getEditorText(viewRef) || yamlText;
        const currentConfig =
          (parseYaml(currentText) as Record<string, unknown>) ?? {};
        const updatedConfig = applyFormFieldToConfig(
          currentConfig,
          field,
          value
        );
        const newYaml = dumpYaml(updatedConfig);
        setYamlText(newYaml);

        if (viewRef.current) {
          syncSourceRef.current = 'form';
          const view = viewRef.current;
          view.dispatch({
            changes: {
              from: 0,
              to: view.state.doc.length,
              insert: newYaml,
            },
          });
          syncSourceRef.current = null;
        }
      } catch {
        // If current YAML is unparseable, just update the field
      }
    },
    [yamlText]
  );

  const handleSubmit = useCallback(async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitError(null);
    setIsSubmitting(true);

    try {
      let parsedConfig: Record<string, unknown>;
      try {
        const currentText = getEditorText(viewRef) || yamlText;
        const parsed = parseYaml(currentText);
        if (!parsed || typeof parsed !== 'object') {
          setSubmitError('Invalid YAML configuration');
          return;
        }
        parsedConfig = parsed as Record<string, unknown>;
      } catch (err) {
        setSubmitError(
          `YAML parse error: ${err instanceof Error ? err.message : String(err)}`
        );
        return;
      }

      const secretsPayload = Object.fromEntries(
        Object.entries(secretValues).filter(([, v]) => v.length > 0)
      );

      const body: Record<string, unknown> = {
        eval_set_config: parsedConfig,
        secrets:
          Object.keys(secretsPayload).length > 0 ? secretsPayload : undefined,
      };

      const response = await apiFetch(
        '/eval_sets/',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
        { returnNonOkResponse: true }
      );

      if (!response) {
        setSubmitError('Could not reach the API. Nothing was launched.');
      } else if (!response.ok) {
        // The API says why (validation detail, permission, quota); show that
        // rather than a generic "check your configuration".
        setSubmitError(
          `Launch rejected (${response.status}): ${await responseDetail(response)}`
        );
      } else {
        try {
          const data: CreateEvalSetResponse = await response.json();
          const evalSetId = data.eval_set_id ?? data.id;
          if (evalSetId) {
            window.location.href = primaryUrl(
              { level: 'eval_set', evalSetId },
              { redirect: true }
            );
            return;
          }
          setSubmitError('Launch succeeded but no eval set ID returned');
        } catch {
          setSubmitError('Unexpected response from server');
        }
      }
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [yamlText, apiFetch, secretValues, primaryUrl]);

  if (cloneId && cloneLoading) {
    return (
      <div className="pane h-full overflow-hidden">
        <LoadingDisplay
          message="Loading configuration…"
          subtitle={`Cloning ${cloneId}`}
        />
      </div>
    );
  }

  // Two panes side by side, the scoring tool's layout: the form (what most
  // launches change) in a fixed column, the YAML taking the rest.
  return (
    <div className="flex h-full min-h-0 flex-col gap-3 overflow-y-auto md:flex-row md:overflow-hidden">
      <section className="pane flex w-full shrink-0 flex-col md:w-80 md:overflow-hidden">
        <PageHeader title="New eval set">
          {cloneId && (
            <span
              className="pane-meta min-w-0 truncate"
              title={`Cloning the config of ${cloneId}`}
            >
              from <span className="font-mono">{cloneId}</span>
            </span>
          )}
        </PageHeader>
        <div className="min-h-0 flex-1 space-y-4 px-4 py-3 md:overflow-y-auto">
          {cloneId && cloneError ? (
            <div role="alert" className="callout callout-err">
              <p className="font-semibold">
                The config of {cloneId} could not be loaded.
              </p>
              <p className="mt-1 text-ink-2">{cloneError.message}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={retryClone}
                >
                  Retry
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-ghost h-auto min-h-6 whitespace-normal py-1"
                  onClick={() => setSearchParams({}, { replace: true })}
                >
                  Start from the template instead
                </button>
              </div>
            </div>
          ) : (
            <p className="text-xs leading-4 text-muted">
              {cloneId
                ? 'The cloned id was cleared so Hawk generates a fresh one; set your own below if you prefer.'
                : 'Tasks, models and everything else live in the YAML; these fields edit the common top-level values and stay in sync with it.'}
            </p>
          )}
          <h3 className="eyebrow">Identity</h3>
          <FormInput
            label="Name"
            hint="Optional label. The eval set id is built from it; blank means “eval-set”."
            value={fields.name}
            onChange={v => handleFieldChange('name', v)}
            placeholder="my-eval-set"
          />
          <FormInput
            label="Eval set id"
            hint="Optional. Blank lets Hawk generate it from the name plus a random suffix. Lowercase letters, digits and hyphens, at most 43 characters."
            value={fields.eval_set_id}
            onChange={v => handleFieldChange('eval_set_id', v)}
            placeholder="generated from the name"
          />
          <h3 className="eyebrow pt-1">Size</h3>
          <div className="grid grid-cols-2 gap-3">
            <FormInput
              label="Limit"
              hint="Run only the first N samples of each task. Blank runs them all."
              value={fields.limit}
              onChange={v => handleFieldChange('limit', v)}
              type="number"
              placeholder="all"
            />
            <FormInput
              label="Epochs"
              hint="Repeat every sample this many times. Blank means once."
              value={fields.epochs}
              onChange={v => handleFieldChange('epochs', v)}
              type="number"
              placeholder="1"
            />
          </div>

          {detectedSecrets.length > 0 && (
            <div className="space-y-3">
              <h3 className="eyebrow pt-1">Secrets</h3>
              <p className="text-xs text-muted">
                Values for the secrets this configuration declares; sent with
                the launch request.
              </p>
              {detectedSecrets.map(secret => (
                <div key={secret.name}>
                  <label
                    htmlFor={`secret-${secret.name}`}
                    className="mb-1 block text-xs font-medium text-ink-2"
                  >
                    {secret.name}
                    {secret.description && (
                      <span className="ml-1 font-normal text-muted">
                        — {secret.description}
                      </span>
                    )}
                  </label>
                  <input
                    id={`secret-${secret.name}`}
                    type="password"
                    value={secretValues[secret.name] || ''}
                    onChange={e =>
                      setSecretValues(prev => ({
                        ...prev,
                        [secret.name]: e.target.value,
                      }))
                    }
                    placeholder={`Enter ${secret.name}`}
                    className="input w-full font-mono"
                  />
                </div>
              ))}
            </div>
          )}

          {yamlParseError && (
            <p className="callout callout-err">
              <span className="font-semibold">YAML error:</span>{' '}
              {yamlParseError}
            </p>
          )}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-3 border-t border-line px-4 py-3">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !!yamlParseError || cloneBlocked}
            title={
              cloneBlocked
                ? 'Resolve the clone above first: retry it or start from the template.'
                : undefined
            }
            className="btn btn-primary px-4"
          >
            {isSubmitting ? 'Launching…' : 'Launch eval set'}
          </button>
          {submitError && (
            // Refusals can name long URLs; wrap them inside the pane.
            <p className="text-xs text-err [overflow-wrap:anywhere]">
              {submitError}
            </p>
          )}
        </div>
      </section>

      <section className="pane flex min-h-96 min-w-0 flex-1 shrink-0 flex-col overflow-hidden md:min-h-0">
        <div className="pane-header">
          <span id="yaml-editor-label" className="pane-title">
            YAML
          </span>
          <span className="pane-meta min-w-0">
            Tasks, models, solvers, limits and secrets. The form edits the same
            document.
          </span>
        </div>
        <div
          ref={editorRef}
          role="group"
          aria-labelledby="yaml-editor-label"
          className="min-h-0 flex-1 overflow-hidden"
        />
      </section>
    </div>
  );
}

function FormInput({
  label,
  hint,
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  label: string;
  /** What the field means and what happens when it is left blank. */
  hint: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
}) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-xs font-medium text-ink-2">
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        aria-describedby={`${id}-hint`}
        className="input w-full"
      />
      <p id={`${id}-hint`} className="mt-1 text-[11px] leading-4 text-muted">
        {hint}
      </p>
    </div>
  );
}
